import angular from 'angular';
import $ from 'jquery';
import _ from 'lodash-es';
import APIV5 from '~/models/api';
import Cache from '~/scripts/services/cache';
import Defaults from '~/scripts/constants/defaults';
import Form from '~/scripts/services/model/form';
import Query from '~/scripts/services/model/query';
import './mf-highlight.filter';
import './mf-input.directive';
import './mf-parser.factory';
import { DEFINED_BY } from '~/scripts/constants/common-kids';
import BaseComponent from '~/scripts/controllers/base-component';
import Lang from '~/scripts/services/model/lang';
import templateModalMfFilterClass from './modal/modal-mf-filter-class.html';
import ModalMfFilterClassController from './modal/modal-mf-filter-class.controller';

/* globals Knitiv */

/**
 * @typedef MfOptions
 * @property {import('@knitiv/api-client-javascript').KNode} [createKid]
 * @property {boolean} [dico]
 * @property {boolean} [displayAddBtn=true]
 * @property {boolean} [displayExpandButton=true]
 * @property {boolean} [displaySuggestBtn=true]
 * @property {boolean} [displaySuggestOnLoad=false]
 * @property {boolean} [displayValues]
 * @property {{
 *   emptyResults: string
 * }} [errors]
 * @property {boolean} [filterClass=false]
 * @property {boolean} [focusSearch]
 * @property {Function} [formatter]
 * @property {boolean} [hideProfil=false]
 * @property {boolean} [icon]
 * @property {boolean} [inherits=true]
 * @property {{
 *   kid: import('@knitiv/api-client-javascript').KNode,
 *   cur: import('@knitiv/api-client-javascript').KNode}} [kpath]
 * @property {boolean} [linkerKid]
 * @property {boolean} [magicCurrent]
 * @property {number} [minLength]
 * @property {boolean} [multipleSelection=false]
 * @property {string} [name]
 * @property {boolean} [openForm]
 * @property {string[]} [specificItems]
 * @property {boolean} [uniqueKid=false]
 */

export default class MfController extends BaseComponent {
	/** @type {import('@knitiv/api-client-javascript').KNodeMF} */
	#oldOrder;

	/** @type {string} */
	#oldPlaceholder;

	/** @type {?import('@knitiv/api-client-javascript').KNodeMF} */
	#oldSuggest;

	/**
	 * Références conservées pour pouvoir désabonner précisément dans $onDestroy.
	 * Sans elles, jQuery ne retrouve pas le handler (.off attend la même référence)
	 * et chaque instance de mf laisse ses listeners sur document / window.
	 *
	 * @type {?() => void}
	 */
	#onBodyScroll = null;

	/** @type {?(event: any) => void} */
	#onDocumentClick = null;

	/** @type {?(event: KeyboardEvent) => void} */
	#onKeyDown = null;

	/** @type {?(event: KeyboardEvent) => void} */
	#onKeyUp = null;

	/** @type {?() => void} */
	#onWindowResize = null;

	/* @ngInject */
	/**
	 * @param {any} $$debounce
	 * @param {import('angular').IAttributes} $attrs
	 * @param {import('angular').ICompileService} $compile
	 * @param {any} $copyToClipboard
	 * @param {import('angular').IDocumentService} $document
	 * @param {import('angular').IRootElementService} $element
	 * @param {import('angular').ILocationService} $location
	 * @param {import('angular').IParseService} $parse
	 * @param {import('angular').IQService} $q
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').IScope} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {import('angular').ui.bootstrap.IPositionService} $uibPosition
	 * @param {import('angular').IWindowService} $window
	 * @param {import('~/scripts/services/form.service').default} FormService
	 * @param {import('~/scripts/services/ktranslate').KTranslate} KTranslate
	 * @param {ReturnType<import('~/scripts/services/ui-action.service').UIActionService>} UIActionService
	 * @param {import('~/scripts/services/model/node-service').NodeService} NodeService
	 * @param {any} Notification
	 * @param {import('~/scripts/services/model/user').UserModel} User
	 */
	constructor(
		$$debounce,
		$attrs,
		$compile,
		$copyToClipboard,
		$document,
		$element,
		$location,
		$parse,
		$q,
		$rootScope,
		$scope,
		$timeout,
		$translate,
		$uibModal,
		$uibPosition,
		$window,
		FormService,
		KTranslate,
		UIActionService,
		NodeService,
		Notification,
		User,
	) {
		super($attrs);

		this.$$debounce = $$debounce;
		this.$attrs = $attrs;
		this.$compile = $compile;
		this.$copyToClipboard = $copyToClipboard;
		this.$document = $document;
		this.$element = $element;
		this.$location = $location;
		this.$parse = $parse;
		this.$q = $q;
		this.$rootScope = $rootScope;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$uibModal = $uibModal;
		this.$uibPosition = $uibPosition;
		this.$window = $window;
		this.FormService = FormService;
		this.KTranslate = KTranslate;
		this.UIActionService = UIActionService;
		this.NodeService = NodeService;
		this.Notification = Notification;
		this.User = User;

		this.#oldOrder = 'ALL';
		this.#oldPlaceholder = '';
		this.#oldSuggest = null;

		/**
		 * Pre-selected match indexes on mouse over or using keyboard navigation
		 *
		 * @type {any[]}
		 */
		this.active = [];

		/**
		 * API Options
		 *
		 * @type {any}
		 */
		this.apiOptions = {};

		/**
		 * Should the typeahead popup be appended to $body instead of the parent element?
		 *
		 * @type {boolean}
		 */
		this.appendToBody = false;

		/**
		 * Should the typeahead popup be appended to an element instead of the parent element?
		 *
		 * @type {?string}
		 */
		this.appendTo = null;

		/**
		 * MF Cardinality
		 *
		 * @type {{from: number, to: number}}
		 */
		this.card = {
			from: 0,
			to:   1,
		};

		/**
		 * Form component structure
		 *
		 * @type {any}
		 */
		this.component = {};

		/**
		 * MF Options
		 *
		 * @type {MfOptions}
		 */
		this.defaultOptions = {
			displayAddBtn:        true,
			displayExpandButton:  false,
			displaySuggestBtn:    true,
			displaySuggestOnLoad: false,
			displayValues:        true,
			focusSearch:          false,
			multipleSelection:    false,
		};

		/** @type {boolean} */
		this.deleting = false;

		/**
		 * Flag to disable ng-model updating
		 *
		 * @type {boolean}
		 */
		this.disableAutoUpdate = false;

		/**
		 * Display selected values under the input control
		 *
		 * @type {boolean}
		 */
		this.displayValues = false;

		/**
		 * Formio component parent
		 *
		 * @type {any}
		 */
		this.formioComponent = undefined;

		/**
		 * Input has focus?
		 *
		 * @type {boolean}
		 */
		this.hasFocus = false;

		/**
		 * L'utilisateur a-t-il tapé dans le champ depuis sa dernière sortie ?
		 *
		 * Armé par l'événement input natif dans mf-input, seul signal fiable d'une frappe :
		 * une écriture programmatique (via $render ou .val()) n'en déclenche jamais.
		 *
		 * @type {boolean}
		 */
		this.userTyped = false;

		/**
		 * Input ng-model options
		 *
		 * @type {{debounce: number, allowInvalid: boolean, updateOn: string}}
		 */
		this.inputModelOptions = {
			allowInvalid: true,
			updateOn:     'default',
			debounce:     200,
		};

		/**
		 * Component key
		 *
		 * @type {string}
		 */
		this.key = '';

		/**
		 * kform controller
		 *
		 * @type {?import('~/components/kform/kform.controller').default}
		 */
		this.kform = null;

		/**
		 * Ktable component structure
		 *
		 * @type {any}
		 */
		this.ktable = {
			query:       undefined,
			forms:       undefined,
			submissions: undefined,
			files:       undefined,
			options:     {
				actions: {},
			},
			cols:        [],
			node:        undefined,
		};

		/**
		 * Last form flag
		 *
		 * @type {boolean}
		 */
		this.lastForm = false;

		/**
		 * Loading state flag
		 *
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * Is Shift key pressed?
		 *
		 * @type {boolean}
		 */
		this.majpressed = false;

		/**
		 * Cursor index of flat array
		 *
		 * @type {number}
		 */
		this.mapIndex = -1;

		/**
		 * Flat-array of path indexes
		 *
		 * @type {any[]}
		 */
		this.mapPath = [];

		/**
		 * Matches displayed in popup
		 *
		 * @type {any[]}
		 */
		this.matches = [];

		/**
		 * Context menu items
		 *
		 * @type {any[]}
		 */
		this.menuOptions = [];

		/**
		 * MF Card attribute
		 *
		 * @type {?string}
		 */
		this.mfCard = null;

		/**
		 * MF Class kid
		 *
		 * @type {?string}
		 */
		this.mfClass = null;

		/**
		 * MF input model
		 *
		 * @type {string | undefined}
		 */
		this.mfInput = undefined;

		/**
		 * MF Linker kid
		 *
		 * @type {string}
		 */
		this.mfLinker = '0';

		/**
		 * MF static results list matches
		 *
		 * @type {any[]}
		 */
		this.mfList = [];

		/**
		 * MF static results list matches from mfParse directive
		 *
		 * @type {any}
		 */
		this.mfParse = {
			enabled: false,
			matches: [],
			parseFromSource: undefined,
		};

		/**
		 * Validators
		 *
		 * @type {any}
		 */
		this.mfValidate = undefined;

		/**
		 * MF mode used, should be 'label' (default) or 'ktable'
		 *
		 * @type {string}
		 */
		this.mode = 'label';

		/**
		 * Default progress type
		 *
		 * @type {boolean}
		 */
		this.moveInProgress = false;

		/**
		 * Allow multiple selection
		 *
		 * @type {boolean}
		 */
		this.multipleSelection = false;

		/**
		 * Disabled control
		 *
		 * @type {boolean}
		 */
		this.ngDisabled = false;

		/**
		 * Model controller
		 *
		 * @type {?angular.INgModelController}
		 */
		this.ngModelCtrl = null;

		/**
		 * ngModel options
		 *
		 * @type {any}
		 */
		this.ngModelOptions = {};

		/**
		 * Avoid dirty check for angular validation
		 *
		 * @type {boolean}
		 */
		this.noDirtyCheck = false;

		/**
		 * @type {any[]}
		 */
		this.oldMatches = [];

		/**
		 * Blur callback
		 *
		 * @type {?Function}
		 */
		this.onBlur = null;

		/**
		 * Create callback
		 *
		 * @type {?Function}
		 */
		this.onCreate = null;

		/**
		 * Delete callback
		 *
		 * @type {?Function}
		 */
		this.onDelete = null;

		/**
		 * Focus callback
		 *
		 * @type {?Function}
		 */
		this.onFocus = null;

		/**
		 * Select callback
		 *
		 * @type {?Function}
		 */
		this.onSelect = null;

		/**
		 * Suggestion pop-up flag
		 *
		 * @type {boolean}
		 */
		this.onSuggestion = false;

		/**
		 * MF Options
		 *
		 * @type {MfOptions | undefined}
		 */
		this.options = undefined;

		/**
		 * MF 'order' parameter to send to API functions
		 *
		 * @type {import('@knitiv/api-client-javascript').KNodeMF}
		 */
		this.order = 'ALL';

		/**
		 * Parser result from $parser servivce
		 *
		 * @type {any}
		 */
		this.parserResult = undefined;

		/**
		 * Input placeholder
		 *
		 * @type {string}
		 */
		this.placeholder = '';

		/**
		 * Popup element
		 *
		 * @type {?import('jquery')}
		 */
		this.$popUpElement = null;

		/**
		 * Results popup position
		 *
		 * @type {any}
		 */
		this.position = {};

		/**
		 * User input query
		 *
		 * @type {?string}
		 * @description we need to propagate user's query so we can higlight matches
		 */
		this.query = null;

		/**
		 * Search class
		 *
		 * @type {{kid: import('@knitiv/api-client-javascript').KNode, name: string}[] | undefined}
		 */
		this.searchClass = undefined;

		/**
		 * Items selected by multi-selection (Ctrl key)
		 *
		 * @type {any[]}
		 */
		this.selectlist = [];

		/**
		 * Display the results popup
		 *
		 * @type {boolean}
		 */
		this.showSuggestion = false;

		/**
		 * MF 'suggest' parameter to send to API functions
		 *
		 * @type {?import('@knitiv/api-client-javascript').KNodeMF}
		 */
		this.suggest = null;

		/**
		 * API specific options for autocomplete
		 *
		 * @type {any}
		 */
		this.mfCustom = {};

		/**
		 * Input title
		 *
		 * @type {string}
		 */
		this.title = '';

		/**
		 * Unique KID?
		 *
		 * @type {boolean}
		 */
		this.uniqueKid = false;

		/**
		 * Use keyboard flag
		 *
		 * @type {boolean}
		 */
		this.useKeyboard = false;

		/**
		 * Selected values (from ng-model)
		 *
		 * @type {any[] | string | undefined}
		 */
		this.values = undefined;

		this.$inputElement = $($element).find('input.form-control');
	}

	/**
	 * Adds a value in the current selected values
	 * TODO: check parent variable
	 *
	 * @param {any} item Item object
	 */
	addValue(item) {
		if (!Array.isArray(this.values)) {
			this.values = [];
		}
		if (Array.isArray(item)) {
			if (this.kform?.files) {
				const files = [];
				for (const index of item) {
					// move i.file to another array in order to avoid watching it
					if ('file' in index) {
						files.push({
							file: index.file,
							name: index.name,
						});
						delete index.file;
					}
				}
				this.kform.addTmpFiles(files);
			}
			this.values.push(...item);
		}
		else {
			this.values.push(item);
		}
		// this.setModelValue(this.values);

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$setValidity('editable', true);
			this.ngModelCtrl.$setValidity('parse', true);
			this.ngModelCtrl.$validate();
		}
	}

	/**
	 * Set flag to determine if results popup should be opened
	 *
	 * @param {boolean} isOpen Set isOpen flag
	 */
	assignIsOpen(isOpen) {
		// @ts-ignore
		this.$scope.mfPopup = isOpen;
	}

	checkValue() {
		let returnValue = false;

		if (this.uniqueKid) {
			if (
				((this.options?.linkerKid && this.values !== '0')
				|| (!this.options?.linkerKid && this.values !== '0')) && this.values !== undefined
			) {
				returnValue = true;
			}
		}
		else if (Array.isArray(this.values) && this.values.length > 0) {
			returnValue = true;
		}

		return returnValue;
	}

	/**
	 * @param {Form} form
	 */
	clearCache(form) {
		if (form?.isa?.objid) {
			this.FormService.mfCacheDelete(form.isa.objid);
			this.resetMatches();
		}
	}

	/**
	 * Collapse the current item to hide child nodes
	 *
	 * @param {any} item Item object
	 */
	collapse(item) {
		item.expanded = false;

		_.remove(
			this.mapPath,
			path => path.length > this.active.length && _.isEqual(this.active, path.slice(0, this.active.length)),
		);
	}

	/**
	 * Send KID to clipboard
	 */
	copyToClipboard() {
		this.$copyToClipboard(this.uniqueKid ? this.values : Array.isArray(this.values) && this.values[0].kid, true);
	}

	/**
	 * Declare the debounced function outside recalculating for proper debouncing
	 */
	debouncedRecalculate() {
		this.$$debounce(() => {
			// if popup is visible
			if (this.matches.length > 0) {
				this.recalculatePosition();
			}

			this.moveInProgress = false;
		}, 200);
	}

	/**
	 * Remove selected value
	 *
	 * @param {any} value Values index
	 * @param {Event} event Browser event
	 */
	delValue(value, event) {
		const index = Array.isArray(this.values)
			? this.values.findIndex(v => v.kid === value.kid && v.isa === value.isa) : -1;

		this.deleting = true;
		if (
			this.kform?.forms
			&& ((this.uniqueKid && !Array.isArray(this.values) && this.values && this.values in this.kform.forms)
				|| (Array.isArray(this.values) && index in this.values && this.values[index].kid in this.kform.forms))
		) {
			delete this.kform.forms[this.values[index].kid];
		}
		const oldValue = this.uniqueKid ? this.values : this.values && { ...this.values[index] };

		if (this.uniqueKid) {
			this.values = undefined;
			if (this.options) {
				this.options.name = undefined;
			}
		}
		else if (Array.isArray(this.values)) {
			this.values.splice(index, 1);
		}

		if (event && this.card.to === 1
			// @ts-ignore
			&& ($(event.target).hasClass('fa-times') || $(event.target).hasClass('mf-delete'))
		) {
			this.mfInput = undefined;
			this.$inputElement.val('');
		}

		this.$timeout(() => {
			this.ngModelCtrl?.$validate();
			this.ngModelCtrl?.$setDirty();

			if (typeof this.onSelect === 'function') {
				this.onSelect({
					$item:  undefined,
					$event: event,
				});
			}
			if (typeof this.onDelete === 'function' && oldValue) {
				this.onDelete({
					$item:  oldValue,
					$event: event,
				});
			}
			if (this.kform && oldValue) {
				const oldKid = this.uniqueKid ? oldValue : oldValue.kid;
				if (oldKid in this.kform.submissions) {
					delete this.kform.submissions[oldKid];
				}
				if (this.kform.currentClassForm in this.kform.forms) {
					this.kform.forms[this.kform.currentClassForm].modified = true;
				}
			}
			this.$element.find('> div').trigger('change');
		});
	}

	/**
	 * Remove all selected values
	 *
	 * @param {Event} event Browser event
	 */
	delAllValues(event) {
		this.deleting = true;
		if (this.kform) {
			if (
				this.uniqueKid
				&& !Array.isArray(this.values)
				&& this.values
				&& this.values in this.kform.forms
			) {
				delete this.kform.forms[this.values];
			}
			else if (Array.isArray(this.values)) {
				for (const index in this.values) {
					if (index in this.values && this.values[index].kid in this.kform.forms) {
						delete this.kform.forms[this.values[index].kid];
					}
				}
			}
		}

		if (this.uniqueKid) {
			this.values = undefined;
			if (this.options) {
				this.options.name = undefined;
			}
		}
		else if (Array.isArray(this.values)) {
			this.values.length = 0;
		}

		this.$timeout(() => {
			this.ngModelCtrl?.$validate();
			this.ngModelCtrl?.$setDirty();

			if (typeof this.onSelect === 'function') {
				this.onSelect({
					$item:  undefined,
					$event: event,
				});
			}
			if (typeof this.onDelete === 'function') {
				this.onDelete({
					$event: event,
				});
			}
			this.$element.find('> div').trigger('change');
		});
	}

	/**
	 * Keep reference to click handler to unbind it.
	 *
	 * @param {Event} event
	 */
	dismissClickHandler(event) {
		// I commented this to avoid multiple MF results (#523)

		// if (
		// 	$(event.target).hasClass('mf-suggestions') ||
		// 	$(event.target).closest('.mf-suggestions').length > 0
		// ) {
		// 	return;
		// }

		// Issue #3973
		// @ts-ignore
		const outsidePopupClick = $(event.target).closest(this.$element).length === 0
			// @ts-ignore
			&& $(event.target).closest(this.$popUpElement).length === 0
			// @ts-ignore
			&& $(event.target).closest(this.$inputElement).length === 0;

		// Firefox treats right click as a click on document
		// noinspection JSUnresolvedVariable
		if (
			(
				// @ts-ignore
				!outsidePopupClick && !$(event.target).hasClass('expand')
				// @ts-ignore
				&& this.multipleSelection === false && event.which !== 3
				&& this.matches.length > 0 && !this.showSuggestion
			)
			|| (this.showSuggestion && this.matches.length === 0)
			|| (
				// @ts-ignore
				outsidePopupClick && this.$inputElement && this.$inputElement[0] !== event.target
				&& (
					(
						// @ts-ignore
						$(event.target).closest('mf').find('mf-popup').scope() && this.$popUpElement.scope()
						// @ts-ignore
						&& this.$popUpElement.scope().$id !== $(event.target).closest('mf').find('mf-popup').scope().$id
					)
					// @ts-ignore
					|| !$(event.target).closest('mf').find('mf-popup').scope()
				)
			)
		) {
			this.resetMatches();

			if (this.card.to === 1) {
				if (!this.uniqueKid && Array.isArray(this.values) && this.values.length > 0) {
					// this.$inputElement.val(this.values[0].name);
					this.mfInput = this.values[0].name;
				}
				else if (this.uniqueKid && this.values) {
					// this.$inputElement.val(this.options.name);
					this.mfInput = this.options?.name;
				}
				else if (!this.hasFocus) {
					// this.$inputElement.val('');
					this.mfInput = undefined;
				}
			}

			this.$timeout(() => {
				this.$scope.$apply();
			});
		}
	}

	/**
	 * Display results in autocomplete/suggestion popup-list
	 *
	 * @param {any} response Response object from API
	 */
	displayResults(response) {
		const results = response.result;
		const onCurrentRequest = this.ngModelCtrl && this.query === this.ngModelCtrl.$viewValue;

		this.recalculatePosition();

		// if ('autocomplete' in response && onCurrentRequest && this.hasFocus) {
		// 	results = response.result;
		// }
		// else if ('suggestion' in response) {
		// 	results = response.result;
		// }

		if (results) {
			let addCreate = typeof this.onCreate === 'function' && !!this.query;
			const cacheMatches = [];

			if ((addCreate && this.query) || this.onSuggestion) {
				const regQuery = addCreate && this.query ? this.query.normalize('NFD')
					.replaceAll(/[\u0300-\u036F]/g, '').toLocaleLowerCase()
					.split(/\W+/)
					.map(word => new RegExp(`^${word}.*`)) : false;

				for (const cache of this.FormService.mfCacheGet(this.order)) {
					if (
						(
							regQuery
							&& regQuery.every(regexp => cache.words.some((/** @type {string} */ word) => regexp.test(word)))
						) || this.onSuggestion
					) {
						cacheMatches.push(_.merge({ icon: 'bookmark-o' }, cache.item));
						if (
							this.query
							&& cache.item.name.toString().toLowerCase().trim() === this.query.toString().toLowerCase().trim()
							&& cache.item.isa === this.order
						) {
							addCreate = false;
						}
					}
				}
			}

			this.matches.length = 0;
			this.matches.push(...cacheMatches);

			if (results.length > 0) {
				this.active.length = 0;
				this.mapIndex = -1;
				this.mapPath.length = 0;

				let { order } = this;
				const { query, options } = this;
				if (order === 'CLASS') {
					order = 'K_NODE;SYS_CLASSE';
				}

				let nextItemSeparator = false;
				let index = 0;
				// add separator if there are cached matches found
				if (cacheMatches.length > 0) {
					nextItemSeparator = true;
				}

				if (options) {
					const { formatter } = options;
					if (formatter) {
						// eslint-disable-next-line unicorn/no-array-callback-reference
						results.map(formatter);
					}
				}
				// eslint-disable-next-line unicorn/consistent-destructuring
				const { profil } = this.$rootScope?.context ?? { profil: undefined };

				for (const result of results) {
					if (typeof result === 'object') {
						if (result.kid === 'ITEM_SEPARATOR') {
							nextItemSeparator = true;
						}
						else if (
							(options?.hideProfil && result.kid !== profil?.kid)
							|| !options?.hideProfil
						) {
							if (nextItemSeparator) {
								result.sep = true;
								nextItemSeparator = false;
							}
							else {
								result.sep = false;
							}
							result.level = 0;
							result.path = [index++];
							result.expanded = false;
							result.matches = [];
							// eslint-disable-next-line unicorn/consistent-destructuring
							this.mapPath.push(result.path);
							// eslint-disable-next-line unicorn/consistent-destructuring
							this.matches.push(result);
							if (query
								&& result.selectable === 1
								&& result.name
								&& result.name.toLowerCase().trim() === query.toLowerCase().trim()
							) {
								addCreate = false;
							}
						}
					}
				}

				// eslint-disable-next-line unicorn/consistent-destructuring
				this.$inputElement.attr('aria-expanded', 'true');
				//
				// if (this.mapPath.length > 0) {
				// 	this.mapIndex = 0;
				// 	this.active = this.mapPath[this.mapIndex];
				// }

				// Select the single remaining option if user input matches
				// if (selectOnExact && this.matches.length === 1 && inputIsExactMatch(this.query, 0)) {
				// 	if (typeof this.debounceUpdate === 'number' || typeof this.debounceUpdate === 'object') {
				// 		$$debounce(() => {
				// 			this.select(this.matches[0], event);
				// 		}, typeof this.debounceUpdate === 'number' ? this.debounceUpdate : this.debounceUpdate.default);
				// 	}
				// 	else {
				// 		this.select(this.matches[0], event);
				// 	}
				// }
			}
			else if (!(this.onCreate && this.query)) {
				this.resetMatches();
			}

			if (this.options && Array.isArray(this.options.specificItems) && this.options.specificItems.length > 0) {
				for (const specificItem of this.options.specificItems) {
					this.matches.unshift({
						kid:        specificItem,
						isa_name:   '',
						name:       this.$translate.instant(`mf.specific-item.${specificItem}`),
						selectable: 1,
						path:       [0],
						specific:   true,
					});
				}
			}

			if (addCreate) {
				this.matches.unshift({
					kid:        '__create',
					isa_name:   '',
					name:       '',
					selectable: 1,
					path:       [0],
				});
				for (const index of Object.keys(this.mapPath)) {
					if (typeof index === 'number') {
						this.mapPath[index][0] += 1;
					}
				}
				this.mapPath.unshift([0]);
			}
			else if (this.matches.length === 0) {
				this.matches.unshift({
					kid:        '__none',
					isa_name:   '',
					name:       this.$translate.instant(this.options?.errors?.emptyResults ?? 'common.no-element-found'),
					selectable: 0,
				});
			}
		}
		if (onCurrentRequest || this.onSuggestion) {
			if (this.onSuggestion) {
				this.showSuggestion = true;
			}
			this.loading = false;
			this.onSuggestion = false;
		}
	}

	// this.$popup.trigger('focus');

	/**
	 * Execute the method
	 *
	 * @param {import('~/scripts/services/model/method').default} method Method instance
	 * @param {import('@knitiv/api-client-javascript').KNode} nodeKid KID of the node
	 * @returns {Promise<any>} Promise result
	 */
	async execMethod(method, nodeKid) {
		if (!method.kid) {
			return false;
		}
		const exec = await APIV5.instance.knmExecute({
			method_kid: method.kid,
			node_kid: nodeKid,
		}, {
			id: 'mf.exec-method',
		});
		return this.$q.when(exec).then(result => {
			if (this.uniqueKid) {
				// @ts-ignore
				this.values = result.kid;
				// @ts-ignore
				this.$inputElement.val(result.value);
			}
			else {
				this.values = [
					{
						// @ts-ignore
						name: result.value,
						isa:  this.options?.createKid || this.order,
					},
				];
			}
			return this.values;
		}).catch(this.$q.reject);
	}

	/**
	 * Expands the current item to display child nodes
	 *
	 * @param {any} item Current item
	 * @returns {Promise<any>} Promise result
	 */
	async expand(item) {
		let mapIndex = this.mapPath.findIndex(index => _.isEqual(index, this.active));
		this.loading = true;
		item.loading = true;
		item.expanded = true;

		const children = await APIV5.instance.mfChildren2(
			item.kid,
			this.onSuggestion && this.suggest ? this.suggest : this.order,
			this.apiOptions,
			{ id: 'mf.expand' },
		);

		return this.$q.when(children).then(response => {
			item.matches = [];
			let nextItemSeparator = false;
			let
				index = 0;

			for (const result of _.merge(response.result)) {
				if (result.kid === 'ITEM_SEPARATOR') {
					nextItemSeparator = true;
				}
				else {
					if (nextItemSeparator) {
						result.sep = true;
						nextItemSeparator = false;
					}
					else {
						result.sep = false;
					}
					result.level = item.level + 1;
					result.path = [...item.path];
					result.path.push(index++);
					this.mapPath.splice(++mapIndex, 0, result.path);
					item.matches.push(result);
				}
			}

			if (this.options?.formatter) {
				// eslint-disable-next-line unicorn/no-array-callback-reference
				item.matches.map(this.options.formatter);
			}

			this.loading = false;
			item.loading = false;
			return this.$q.resolve(item.matches);
		}).catch(() => {
			this.loading = false;
			item.loading = false;
			this.$q.reject();
		});
	}

	/**
	 * Recalculate for proper debouncing
	 */
	fireRecalculating() {
		if (!this.moveInProgress) {
			this.moveInProgress = true;
			this.$scope.$digest();
		}

		this.debouncedRecalculate();
	}

	/**
	 * @param {any} item
	 * @param {any[]|string} path
	 * @param {any} [definition]
	 * @returns {any}
	 */
	getPath(item, path, definition) {
		if (!item) {
			return definition || undefined;
		}
		if (!path) {
			return item;
		}

		// If the path is a string, turn it into an array.
		if (typeof path === 'string') {
			// eslint-disable-next-line no-param-reassign
			path = path.split('.');
		}
		// If the path is an array, take the first element, and recurse its path
		if (Array.isArray(path)) {
			const current = path.shift();
			if (Object.prototype.hasOwnProperty.call(item, current)) {
				// If there are no more path items, stop here.
				if (path.length === 0) {
					return item[current];
				}

				return this.getPath(item[current], path);
			}
		}
		return undefined;
	}

	getValues() {
		if (typeof this.values === 'object' && !Array.isArray(this.values)) {
			return [this.values];
		}
		return this.values;
	}

	/**
	 * @param {Event} event
	 */
	async openFilterClass(event) {
		event.stopPropagation();
		this.resetMatches();

		await this.$uibModal.open({
			ariaLabelledBy:  'modal-mf-filter-class-title',
			ariaDescribedBy: 'modal-mf-filter-class-body',
			template:        templateModalMfFilterClass,
			controller:      ModalMfFilterClassController,
			controllerAs:    '$ctrl',
			size:            'sm',
			resolve:         {
				mf: this,
			},
		}).result;

		this.order = this.searchClass ? `FOCUS:${this.searchClass[0].kid}` : this.#oldOrder;
		this.suggest = this.searchClass ? `FOCUS:${this.searchClass[0].kid}` : this.#oldSuggest;
		this.placeholder = this.searchClass ? this.searchClass[0].name : this.#oldPlaceholder;
	}

	/**
	 * @param {any} [newValue]
	 */
	openCreateForm(newValue = undefined) {
		if (typeof newValue !== 'object') {
			// eslint-disable-next-line no-param-reassign
			newValue = {
				// name:   '< Nom à venir... >',
				isa:    this.options?.createKid || this.order,
				orphan: this.lastForm,
			};
			if (Array.isArray(this.values) && !this.uniqueKid) {
				this.values.push(newValue);
			}
		}

		if (this.kform && this.component) {
			const form = this.kform.forms[this.kform.currentClassForm];
			const { key } = this.component;
			const sparent = {
				create:     form.create,
				field:      key,
				isa:        form.isa,
				k:          Array.isArray(this.values) ? this.values.length - 1 : undefined,
				kid:        form.kid,
				kndef:      form.kndef,
				linker_kid: this.mfLinker,
				name:       form.getName(),
			};

			if (key in form.map && form.map[key].lastForm) {
				/** @type {any} */
				const newKid = this.options?.createKid || this.order;
				if (newKid.startsWith('K_NODE;')) {
					// @ts-ignore
					const newForm = this.FormService.create(newKid, { new_kid: newValue?.new_kid });
					newForm.name = this.query || '';
					newForm.setName(this.query);
					newForm.lastForm = true;
					newForm.modified = true;

					for (const link of form.link) {
						if (
							link.objnum2_kid === newForm.isa.objid
							&& link.objnum2_kid === form.map[key].kid
							&& link.linker_kid === form.map[key].linker_kid
						) {
							newForm.isa.objname = link.objname;
							newForm.isa.lang = new Lang(link.lang);
						}
					}

					// TODO: should I instance a Node object here?
					// @ts-ignore
					newForm.parent = sparent;

					if (newForm.kid) {
						newValue.kid = newForm.kid;
						this.kform.forms[newForm.kid] = newForm;
						this.kform.submissions[newForm.kid] = {
							data: {
								KREPRE: newForm.repre,
							},
						};
					}
				}
			}
			else {
				/** @type {any} */
				const items = this.FormService.getItemsForSave(this.kform.forms, this.kform.submissions);
				items[0].submission['K_NODE;_new_'] = {
					data: {
						KREPRE: [
							{
								lang: {
									kid: 'K_NODE;UNIVERSEL',
									isa: 'K_NODE;SYS_CLASSE',
								},
								readonly: false,
								type: 'K_NODE;STRING',
								value: this.query,
							},
						],
					},
				};
				(async () => this.kform?.addForm(newValue, sparent, { items }))();
			}
		}
	}

	/**
	 * @param {any} index
	 * @param {any} nodeItem
	 * @param {Event} event
	 * @returns {any}
	 */
	openForm(index, nodeItem, event) {
		event.stopPropagation();
		if (this.kform) {
			return this.kform.openForm(nodeItem.kid, {
				create:     this.kform.create,
				kid:        this.kform.currentClassForm,
				isa:        {
					objid: this.options?.createKid || this.order,
				},
				linker_kid: this.mfLinker,
				field:      this.component?.key,
				k:          index,
			});
		}

		return this.$location.path(`/form/${nodeItem.kid}`);
	}

	/**
	 * Recalculate actual position and set new values to scope after digest loop is popup in right position
	 */
	recalculatePosition() {
		this.position = this.appendToBody
			? this.$uibPosition.offset(this.$inputElement) : this.$uibPosition.position(this.$inputElement);
		this.position.top += this.$inputElement.prop('offsetHeight');
	}

	/**
	 * Reset the matches array result
	 */
	resetMatches() {
		if (this.matches.length > 0 && !this.showSuggestion) {
			this.oldMatches = this.matches;
			this.oldMapPath = this.mapPath;
		}
		this.showSuggestion = false;
		this.matches.length = 0;
		this.active.length = 0;
		this.multipleSelection = this.options?.multipleSelection ?? false;
		// this.mapPath.length = 0;
		this.mapIndex = -1;

		this.useKeyboard = false;
		this.$inputElement.attr('aria-expanded', 'false');
	}

	/**
	 * User selects the match item from popup-list
	 *
	 * @param {any} item Item object
	 * @param {any|Event} event jQuery browser event
	 */
	select(item, event) {
		// Find in values if this element is already set
		let found = false;
		let replaceIndex = -1;

		// Backup existing values
		const oldValues = Array.isArray(this.values) ? [...this.values] : this.values;

		/** @type {any} */
		const locals = {};
		if (this.card.to > 1 && Array.isArray(this.values)) {
			found = this.values.some(value => value.kid === item.kid);
			if (found) {
				this.$inputElement.val('');
				this.$inputElement.trigger('focus');
				return;
			}
		}
		else if (this.card.to === 1 && this.values && (this.uniqueKid ? this.values : this.values.length >= 0)) {
			replaceIndex = 0;
		}

		if (this.parserResult) {
			locals[this.parserResult.itemName] = item;
			this.parserResult.modelMapper(this.$scope, locals);
		}

		if (event) {
			// eslint-disable-next-line no-param-reassign
			event = Object.create(event, {
				target: {
					value: this.$inputElement,
				},
			});
		}

		if (item.kid === '__create') {
			if (item.name !== '') {
				this.query = item.name;
			}
			this.$inputElement.val(this.query || '');
			/** @type {any} */
			let newValue;
			if (this.card.to === 1 || !Array.isArray(this.values)) {
				this.values = [];
			}
			if (!this.uniqueKid) {
				newValue = {
					isa:     this.options?.createKid || this.order,
					name:    this.query,
					new_kid: item.new_kid,
					orphan:  this.lastForm,
				};
				this.values.push(newValue);
			}

			if (typeof this.onCreate === 'function') {
				this.onCreate({
					$query: this.query,
					$event: event,
					$input: this.$inputElement,
					info:   {
						class_kid:  this.mfClass,
						linker_kid: this.mfLinker,
					},
				});
				if (this.displayValues) {
					this.$inputElement.val('');
				}
			}

			this.openCreateForm(newValue);
		}
		else {
			item.class_kid = this.mfClass;
			item.linker_kid = this.mfLinker;

			if (this.displayValues) {
				this.mfInput = undefined;
			}
			else {
				this.$inputElement.val(item.name);
			}

			if (replaceIndex >= 0) {
				if (this.uniqueKid) {
					this.values = item.kid;
					if (this.options) {
						this.options.name = item.name;
					}
				}
				else {
					if (!Array.isArray(this.values)) {
						this.values = [];
					}
					this.values[replaceIndex] = {
						kid:      item.kid,
						isa:      item.isa,
						isa_name: item.isa_name,
						name:     item.name,
						orphan:   item.orphan,
					};
				}
			}
			else {
				const newItem = {
					kid:      item.kid,
					isa:      item.isa,
					isa_name: item.isa_name,
					name:     item.name,
					orphan:   item.orphan,
				};

				if (this.uniqueKid) {
					this.values = newItem.kid;
					if (this.options) {
						this.options.name = newItem.name;
					}
				}
				else {
					if (!Array.isArray(this.values)) {
						this.values = [];
					}
					if (this.card.to === 1 && this.values.length === 1) {
						this.values[0] = newItem;
					}
					else {
						this.values.push(newItem);
					}
				}
			}

			if (typeof this.onSelect === 'function') {
				this.onSelect({
					$item:  item,
					$event: event,
				});
			}
		}

		this.$timeout(() => {
			if (this.ngModelCtrl) {
				this.ngModelCtrl.$setValidity('editable', true);
				this.ngModelCtrl.$setValidity('parse', true);
				if (!_.isEqual(this.values, oldValues)) {
					if (this.kform && this.kform.currentClassForm in this.kform.forms) {
						this.kform.forms[this.kform.currentClassForm].modified = true;
					}
					this.ngModelCtrl.$setDirty();
				}
				// this.setModelValue(this.values);
				this.ngModelCtrl.$validate();
			}
			else {
				// this.setModelValue(this.values);
			}
			if (item.kid !== 'create') {
				this.$element.find('> div').trigger('change');
			}
		});
		this.resetMatches();
	}

	/**
	 * Select manually the element on popup
	 *
	 * @param {number} index Match index
	 */
	selectValue(index) {
		if (Array.isArray(this.values)) {
			for (const [index2, value] of this.values.entries()) {
				value.select = index2 === index;
			}
		}
	}

	/**
	 * model setter executed upon match selection
	 *
	 * @param {any[]|string} newValue New values to set
	 * @returns {any} Model parsed
	 */
	setModelValue(newValue) {
		const parsedModel = this.$parse(this.$attrs.ngModel);
		const invokeModelSetter = this.$parse(`${this.$attrs.ngModel}($$$p)`);

		if (this.options && Array.isArray(newValue)) {
			if (this.uniqueKid) {
				// eslint-disable-next-line no-param-reassign
				newValue = newValue.length > 0 ? newValue[0].kid : undefined;
			}
			if (this.options?.name && this.$attrs.options?.name) {
				this.$parse(this.$attrs.options.name).assign(this.$scope, newValue.length > 0 ? newValue[0].name : '');
			}
		}

		if (typeof parsedModel(this.$scope) === 'function'
			&& this.ngModelOptions.getOption('getterSetter')) {
			return invokeModelSetter(this.$scope, { $$$p: newValue });
		}

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$setViewValue(newValue);
		}

		return parsedModel.assign ? parsedModel.assign(this.$scope, newValue) : null;
	}

	/**
	 * Set cardinality from "card" binding
	 */
	setCard() {
		const tmpcard = this.mfCard ? /^(\d+)-(\d+|N)$/.exec(this.mfCard) : [null, '0', '1'];

		if (this.mfCard && tmpcard && tmpcard[1] && tmpcard[2]) {
			this.card.from = Number.parseInt(tmpcard[1], 10);
			this.card.to = tmpcard[2] === 'N' ? Defaults.KCARDN : Number.parseInt(tmpcard[2], 10);

			if ((this.options && !('displayValues' in this.options)) || !this.options) {
				this.displayValues = this.card.to !== 1 || (this.formioComponent && this.formioComponent.readOnly);
			}

			if (this.ngModelCtrl) {
				this.ngModelCtrl.$validate();
			}
		}
	}

	/**
	 * Build query
	 *
	 * @returns {Promise<any>} Promise result
	 */
	async setQuery() {
		const isa = this.options?.createKid || this.order;
		if (!isa.startsWith('K_NODE;')) {
			console.warn(`Unable to use query with ${isa}`);
			return;
		}

		// @ts-ignore
		this.ktable.node = await this.NodeService.loadDef(isa);
		if (this.component) {
			let ddparent = _.merge(this.$scope.$parent);
			let definedby = ddparent.definedby || undefined;

			while (ddparent && !ddparent.definedby) {
				ddparent = ddparent.$parent;
				definedby = ddparent ? ddparent.definedby : undefined;
			}

			if (!definedby) {
				definedby = [];
			}

			for (const col of this.ktable.node.link.filter((/** @type {any} */ link) => link.flow === 'from'
				&& link.relation_kid === DEFINED_BY).map((/** @type {any} */ v) => {
				const returnValue = {
					kid:         v.objnum2_kid,
					name:        v.objname,
					linker_kid:  v.linker_kid,
					type_kid:    v.type_kid,
					linker_info: undefined,
				};

				if (v.linker_kid !== '0') {
					returnValue.linker_info = v.linker_info;
				}

				return returnValue;
			})) {
				definedby.push(col);
			}

			if (definedby) {
				const select = [];

				// Ajout automatique d'une colonne cachée contenant le kid de l'objet
				if (!Array.isArray(this.ktable.cols)
					|| (Array.isArray(this.ktable.cols)
						&& !this.ktable.cols.includes(this.ktable.node.kid))) {
					select.push({
						inherits_extended: -1,
						kid:               this.ktable.node.isa.objid,
						hidden:            true,
						name:              this.ktable.node.isa.objname,
						node:              'REPRE',
						op:                'detail',
						type:              this.ktable.node.type_kid,
					});
				}

				for (const col of this.ktable.cols) {
					if (
						this.ktable.node.kid === col.kid && this.ktable.node.linker_kid === col.linker_kid
						&& this.ktable.query.select.length > 0
					) {
						this.ktable.query.select[0].hidden = false;
					}
					else {
						/** @type {any} */
						const s = {
							inherits_extended: -1,
							kid:               col.kid,
							linker_kid:        col.linker_kid,
							hidden:            false,
							name:              col.name,
							node:              'REPRE',
							op:                'detail',
							path:              [
								{
									kid:        this.ktable.node.isa.objid,
									name:       this.ktable.node.isa.objname,
									flow:       undefined,
									linker_kid: '0',
								},
							],
							type:              col.type_kid,
						};

						if (this.ktable.node.isa.objid !== col.kid) {
							s.path.push({
								kid:        col.kid,
								name:       col.name,
								flow:       'down',
								linker_kid: col.linker_kid || '0',
							});
						}

						if (col.linker_kid !== '0') {
							s.linker_info = col.linker_info;
						}

						select.push(s);
					}
				}

				if (this.ktable.cols.length === 0
					&& select.length > 0 && select[0].kid === this.ktable.node.isa.objid) {
					select[0].hidden = false;
				}

				this.ktable.query = new Query({
					select,
					from:  [
						{
							node:              this.ktable.node.isa.objid,
							name:              this.ktable.node.isa.objname,
							type:              this.ktable.node.type_kid,
							inherits_extended: -1,
						},
					],
					where: undefined,
					date:  '_NOW_',
				});
				this.ktable.options.mode = 'normal';
				this.ktable.options.actions.upload = this.ktable.node.uploadFile;
				if (this.formioComponent) {
					this.formioComponent.krepre_mode = this.ktable.node.krepre.mode;
				}

				for (const link of definedby) {
					if (link.objnum2_kid === this.kform?.forms[this.kform?.currentClassForm]?.isa.objid) {
						this.ktable.filter = link;
						this.ktable.options.mode = 'component_of';
						if (this.ktable.options?.parent) {
							this.ktable.options.parent.linker_kid = link.linker_kid;
						}
					}
				}
			}

			// @ts-ignore
			if (!this.$scope.builder) {
				this.setQueryWhere();
			}

			this.ktable.options.mode = 'normal';
		}
	}

	/**
	 * Build query where
	 */
	setQueryWhere() {
		const values = [];
		if (Array.isArray(this.values)) {
			for (const v of this.values) {
				if (!v.kid.startsWith('K_NODE;_NR_')) {
					values.push(_.merge(v, { isa: this.options?.createKid || this.order }));
				}
			}
		}

		if (!this.ktable.query) {
			this.ktable.query = new Query();
		}
		this.ktable.query.where.rules = [
			{
				condition:  'NONE',
				field:      this.options?.createKid || this.order,
				having:     undefined,
				id:         this.options?.createKid || this.order,
				label:      this.component.label, // TODO check label
				operator:   'in',
				// path:       [
				// 	{
				// 		kid:        this.options.createKid || this.order,
				// 		name:       this.component.label,
				// 		flow:       null
				// 	}
				// ],
				type:       'K_NODE;STRING',
				value:      values,
				linker_kid: '0',
			},
		];
	}

	/**
	 * Set the values
	 *
	 */
	setValues() {
		const setItems = async (/** @type {string | any[] | undefined} */ items_) => {
			if (this.uniqueKid) {
				if (items_ === undefined) {
					if (this.options?.linkerKid && this.values !== '0') {
						this.values = '0';
					}
					this.$inputElement.val('');
					return this.options?.linkerKid ? '0' : undefined;
				}
				else if (items_ && this.options?.name) {
					return [{ kid: items_, name: this.options.name }];
				}
				else if (typeof items_ === 'string') {
					if (items_.startsWith('K_NODE;')) {
						this.disableAutoUpdate = true;
						this.KTranslate.remove(this.$scope.$id);

						// @ts-ignore
						this.$scope.setText = text => {
							this.disableAutoUpdate = false;
							return [{ kid: items_, name: text }];
						};
						const text = await this.KTranslate.request(this.$scope, items_);
						this.disableAutoUpdate = false;
						return [{ kid: items_, name: text }];
					}
					else if (items_ === '_KID_') {
						return [{ kid: items_, name: this.$translate.instant('query.current-item') }];
					}
				}
			}
			else if (Array.isArray(items_)) {
				return items_;
			}
			else if (items_ && typeof items_ === 'object' && 'kid' in items_) {
				return [items_];
			}
			else {
				this.$inputElement.val('');
				return [];
			}
		};

		if (this.disableAutoUpdate) {
			this.disableAutoUpdate = false;
		}
		else {
			setItems(this.values).then(newItems => {
				if (Array.isArray(newItems)) {
					if (this.ngModelCtrl) {
						this.ngModelCtrl.$setValidity('editable', true);
						this.ngModelCtrl.$setValidity('parse', true);
					}

					newItems.map(item => {
						if (item.kid === '_KID_' && !item.name) {
							item.name = this.$translate.instant('query.current-item');
						}
						return item;
					});

					if (this.options) {
						if (newItems.length === 1 && this.card.to === 1) {
							this.options.name = newItems[0].name;
						}
						else if (newItems.length === 0) {
							this.options.name = undefined;
						}

						this.$timeout(() => {
							// setValues() peut être déclenché par une résolution asynchrone du
							// modèle — typiquement le type par défaut de la barre de recherche,
							// qui arrive après le chargement de la page. Écrire dans le DOM à ce
							// moment-là écrase la saisie en cours ; et comme .val() n'émet pas
							// d'événement input, le modèle et le champ divergent, si bien
							// qu'aucune requête d'autocomplétion n'est plus émise pour le texte
							// tapé. Le champ est réconcilié au blur et au clic extérieur.
							//
							// La garde porte sur la saisie, pas sur le focus : un champ
							// simplement survolé du curseur doit afficher la valeur que le
							// modèle vient de résoudre, sans quoi une valeur contextualisée à
							// l'ouverture d'un formulaire reste invisible tant que l'utilisateur
							// n'a pas cliqué ailleurs.
							if (this.hasFocus && this.userTyped) {
								return;
							}
							this.$inputElement.val(this.options?.name || '');
						});
					}
				}
				return this.$q.resolve();
			}).catch(this.$q.reject);
		}

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$setValidity('editable', true);
			this.ngModelCtrl.$setValidity('parse', true);
			this.ngModelCtrl.$validate();
		}
	}

	/**
	 * Show popup-list for suggestions or autocompletion results
	 *
	 * @param {boolean} [toggle]
	 * @returns {Promise<any>} Promise result
	 */
	async showListSuggestions(toggle = false) {
		let returnValue;

		if (!this.showSuggestion) {
			this.query = null;
			this.onSuggestion = true;
			if (this.mfParse.enabled) {
				await this.$timeout(() => {
					this.displayResults({
						result: this.mfParse.matches,
					});
					this.onSuggestion = true;
				});
			}
			else if (this.mfList?.length > 0) {
				await this.$timeout(() => {
					this.displayResults({
						result: this.mfList.map(item => _.extend(item, { selectable: 1 })),
					});
					this.onSuggestion = true;
				});
			}
			else {
				returnValue = null;
				if (this.suggest) {
					this.loading = true;
					const suggestions = await APIV5.instance.mfSuggestion2(
						this.suggest,
						this.apiOptions,
						{ id: 'mf.show-list-suggestions' },
					);

					this.$q
						.when(suggestions)
						.then(result => {
							this.displayResults(result);
							this.onSuggestion = true;
							return true;
						})
						.catch(error => {
							console.error(error);
						});
				}
				else {
					this.$q.reject();
				}
			}
		}
		// if suggestions shown and toggle
		// then hide
		else if (toggle) {
			this.showSuggestion = false;
		}

		return returnValue;
	}

	/**
	 * Validator on card (for card *-N except 1-1)
	 *
	 * @param {any[] | string} modelValue Model value
	 * @param {any[] | string} viewValue View value
	 * @returns {boolean} Is valid
	 */
	validatorCard(modelValue, viewValue) {
		let valid = true;
		const values = modelValue || viewValue;

		if (this.component && this.component.kdata && !this.component.kdata.submit) {
			return true;
		}

		if (this.card.from !== 1 && Array.isArray(values)) {
			if (values.length < this.card.from || (this.card.to !== Defaults.KCARDN && values.length > this.card.to)) {
				if (this.ngModelCtrl && !this.ngModelCtrl.$pristine) {
					this.$translate('error.invalid-card-mf', {
						from: this.card.from,
						to:   this.card.to === Defaults.KCARDN ? 'N' : this.card.to,
					}).then(t => {
						this.component.customError = t;
						return this.$q.resolve();
					}).catch(this.$q.reject);
				}
				valid = false;
			}
			else {
				this.component.customError = undefined;
			}
		}

		return valid;
	}

	/**
	 * Custom validator
	 *
	 * @param {any[] | string} modelValue Model value
	 * @param {any[] | string} viewValue View value
	 * @returns {boolean} Is valid
	 */
	validatorCustom(modelValue, viewValue) {
		let valid = true;
		let input;
		/* eslint-disable no-unused-vars */
		// noinspection JSUnusedLocalSymbols
		const values = modelValue || viewValue; // jshint ignore:line

		if (this.component && this.component.kdata && !this.component.kdata.submit) {
			return true;
		}

		if (values && values.length === 1 && values[0].kid && values[0].kid.startsWith('K_NODE;_NR_')) {
			// noinspection JSUnusedAssignment
			[input] = values;

			// FOR-255 - Enable row data and form data to be visible in the validator.
			// noinspection JSUnusedLocalSymbols
			// const data = scope.submission.data; // jshint ignore:line
			// noinspection JSUnusedLocalSymbols
			// const row = scope.data; // jshint ignore:line
			const { custom } = this.component.validate;
			if (custom) {
				// TODO: get data from scope.submission in Formio instance
				// custom = custom.replace(/({{\s*(.*\S)\s*}})/g, (match, $1, $2) => this.getPath(data, $2));

				try {
					// eslint-disable-next-line no-eval
					eval(custom);
				}
				catch (/** @type {any} */ error) {
					valid = error.message;
				}

				if (!('component' in this.$scope)) {
					this.component = {
						customError: undefined,
					};
				}

				if (valid !== true) {
					this.component.customError = valid;
					return false;
				}
			}
		}

		return true;
	}

	/**
	 * Validator on required (for card 1-1)
	 *
	 * @param {any[]|string} modelValue Model value
	 * @param {any[]|string} viewValue View value
	 * @returns {boolean} Is valid
	 */
	validatorRequired(modelValue, viewValue) {
		let valid = true;
		const values = modelValue || viewValue;

		// @ts-ignore
		// noinspection JSUnresolvedVariable
		if (this.component && this.component.kdata && !this.component.kdata.submit && !this.$scope.builder) {
			return true;
		}

		if (this.card.from === 1) {
			if (Array.isArray(values)) {
				valid = values.length > 0;
			}
			else if (!values) {
				valid = false;
			}
		}

		if (valid && this.component) {
			this.component.customError = undefined;
		}

		return valid;
	}

	/**
	 * @param {KeyboardEvent} event
	 */
	valueOnKeyDown(event) {
		if (!((event.ctrlKey || event.metaKey) && event.key === 'c')) {
			event.preventDefault();
		}
	}

	$doCheck() {
		if (this.options?.displayValues) {
			this.displayValues = this.card.to !== 1 || (this.formioComponent && this.formioComponent.readOnly);
		}

		if (this.options?.uniqueKid && this.options.uniqueKid !== this.uniqueKid) {
			this.uniqueKid = this.options.uniqueKid;
			this.setCard();
			if (this.uniqueKid) {
				if (Array.isArray(this.values) && this.values.length > 0) {
					this.$inputElement.val(this.values[0].name);
					this.values = this.values[0].kid;
				}
				else if (typeof this.values !== 'string' || !this.values.startsWith('K_NODE;')) {
					this.values = undefined;
				}
				// force card.to to 1
				this.card.to = 1;
				this.displayValues = false;
			}
			else {
				if (!Array.isArray(this.values)) {
					this.values = this.values ? [{ kid: this.values, name: this.options.name }] : [];
					if (this.displayValues) {
						this.$inputElement.val('');
					}
				}
				if (this.options.displayValues) {
					this.displayValues = this.card.to !== 1 || (this.formioComponent && this.formioComponent.readOnly);
				}
			}
		}

		if (this.mode === 'ktable') {
			this.setQueryWhere();
		}
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.mfCard) {
			this.setCard();
		}

		if (changes.order || changes.mode) {
			// if suggest equals order, then copy new order
			if (
				changes.order
				&& !changes.order.isFirstChange()
				&& changes.order.previousValue === this.suggest
				&& this.suggest !== this.order
			) {
				this.suggest = this.order;
				this.#oldSuggest = this.suggest;
			}

			if (this.mode === 'ktable') {
				this.setQuery();

				// @ts-ignore
				if (!this.$scope.builder) {
					this.$scope.$watch('$ctrl.component.ktable.cols', (newCols, oldCols) => {
						// @ts-ignore
						if (!this.$scope.builder && this.ktable.node && newCols && !_.isEqual(newCols, oldCols)) {
							this.setQueryWhere();
						}
					}, true);
				}
			}

			// reset selected values if order is dfferent than previous one
			if (
				changes.order
				&& !changes.order.isFirstChange()
				&& changes.order.previousValue !== changes.order.currentValue
			) {
				if (Array.isArray(this.values) && this.values.length > 0) {
					this.values.length = 0;
				}
				else if (this.uniqueKid && this.values) {
					this.values = undefined;
				}
			}
		}

		if (changes.options && this.options) {
			Object.assign(this.options, _.extend(this.defaultOptions, this.options));
			this.multipleSelection = this.options.multipleSelection ?? false;
		}
	}

	$onDestroy() {
		if (this.#onDocumentClick) {
			// @ts-ignore
			this.$document.off('click', this.#onDocumentClick);
			this.#onDocumentClick = null;
		}

		if (this.#onKeyDown) {
			this.$window.removeEventListener('keydown', this.#onKeyDown);
			this.#onKeyDown = null;
		}

		if (this.#onKeyUp) {
			this.$window.removeEventListener('keyup', this.#onKeyUp);
			this.#onKeyUp = null;
		}

		if (this.appendToBody || this.appendTo) {
			this.$popup?.remove();
		}

		if (this.#onWindowResize) {
			$(this.$window).off('resize', this.#onWindowResize);
			this.#onWindowResize = null;
		}

		if (this.#onBodyScroll) {
			this.$document.find('body').off('scroll', this.#onBodyScroll);
			this.#onBodyScroll = null;
		}
		// Prevent jQuery cache memory leak
		// @ts-ignore
		this.$popUpElement.remove();
	}

	$onInit() {
		const HOT_KEYS = new Set([
			'Tab',
			'Enter',
			'NumpadEnter',
			'ShiftLeft',
			'ShiftRight',
			'ControlLeft',
			'ControlRight',
			'AltLeft',
			'AltRight',
			'Escape',
			'ArrowUp',
			'ArrowDown',
			'ArrowRight',
			'ArrowLeft',
		]);

		/**
		 * @type {import('angular').IScope}
		 * @description
		 *   create a child scope for the MF directive so we are not polluting original scope
		 *   with MF-specific data (matches, query etc.)
		 */
		const scope = this.$scope.$new();

		// @ts-ignore
		this.$scope.mfPopup = false;

		const parent = scope.$parent;
		// @ts-ignore
		const form = parent.form || undefined; // jshint ignore:line

		this.#onDocumentClick = event => this.dismissClickHandler(event);
		// @ts-ignore
		this.$document.on('click', this.#onDocumentClick);

		this.#oldOrder = this.order;
		this.#oldSuggest = this.suggest;
		this.#oldPlaceholder = this.placeholder;

		// bind events only if appendToBody params exist - performance feature
		if (this.appendToBody) {
			this.#onWindowResize = () => this.fireRecalculating();
			this.#onBodyScroll = () => this.fireRecalculating();
			$(this.$window).on('resize', this.#onWindowResize);
			this.$document.find('body').on('scroll', this.#onBodyScroll);
		}

		if (this.mfCustom) {
			this.apiOptions.MF_CUSTOM = this.$scope.$eval(this.mfCustom);
		}

		if (this.options?.inherits !== undefined) {
			this.apiOptions.inherits = this.options.inherits;
		}

		if (this.options?.magicCurrent !== undefined) {
			this.apiOptions.magic_current = this.options.magicCurrent;
		}

		if (this.options?.dico !== undefined) {
			this.apiOptions.dico = this.options.dico;
		}

		// TODO: ceci est du bricolage... je n'ai pas réussi à trouver comment faire mieux pour accéder au formioComponent
		if (this.$scope.$parent.$parent && 'component' in this.$scope.$parent.$parent) {
			this.formioComponent = this.$scope.$parent.$parent;
			this.component = this.formioComponent.component;
			// @ts-ignore
			this.$scope.builder = !!this.formioComponent.builder;
			// @ts-ignore
			this.$scope.componentId = this.formioComponent.componentId;

			if (this.component?.kdata?.create === false) {
				this.onCreate = null;
			}
		}

		// @ts-ignore
		this.$scope.mfPopup = false;
		// this.$scope.mfSelect = this.mfSelect;

		// scope.$on('$destroy', this.$scope.$on('$destroy', () => {
		// 	scope.$destroy();
		// }));

		// After form submission, update all KID-generated "K_NODE;_NR_xxx" by "K_NODE;yyy"
		this.$scope.$on('Form.nrReplace', (event, nr) => {
			for (const [newKid, oldKid] of Object.entries(nr)) {
				if (Array.isArray(this.values)) {
					for (const value of this.values) {
						if (value.kid === oldKid) {
							value.kid = newKid;
						}
					}
				}
				else if (this.uniqueKid && this.values === oldKid) {
					this.values = newKid;
				}
			}
		});

		if (this.component) {
			this.$scope.$on('Form.updateComponent', (event, component) => {
				if (component.key === this.component.key) {
					_.merge(this.component, component);
				}
			});
		}

		if (
			'component' in scope
			// @ts-ignore
			&& 'validate' in scope.component
			// @ts-ignore
			&& 'card' in scope.component.validate
		) {
			// @ts-ignore
			this.mfCard = scope.component.validate.card;
		}

		this.$scope.$watch('$ctrl.options.kpath', kpath => {
			if (this.options?.kpath && kpath && 'kid' in kpath) {
				this.apiOptions.kpath_kid = this.options.kpath.kid;
				this.apiOptions.kpath_cur = this.options.kpath.cur;
			}
		}, true);

		// WAI-ARIA
		const popupId = `mf-${scope.$id}-${Math.floor(Math.random() * 10_000)}`;

		this.$inputElement.attr({
			'aria-autocomplete': 'list',
			'aria-expanded':     false,
			'aria-owns':         popupId,
		});

		if ('focus' in this.$attrs || this.options?.focusSearch) {
			this.$timeout(() => {
				this.$inputElement.trigger('focus');
			});
		}

		if (this.options?.displaySuggestOnLoad === true) {
			this.$timeout(() => this.showListSuggestions());
		}

		// @ts-ignore
		if (this.$rootScope?.context?.profil?.admin) {
			this.menuOptions.push({
				text:  `<i class="fa fa-eraser"></i> ${this.$translate.instant('mf.clear-cache')}`,
				click: () => {
					Cache.clear();
					this.clearCache(form);
					this.Notification.info({
						title:   this.$translate.instant('common.info'),
						message: this.$translate.instant('mf.info-cache-cleared'),
					});
				},
			});
		}

		this.menuOptions.push({
			text:  `<i class="fa fa-clipboard"></i> ${this.$translate.instant('mf.copy-kid')}`,
			enabled: this.uniqueKid ? this.values : this.values && this.values.length > 0,
			click: () => this.copyToClipboard(),
		});

		// @ts-ignore
		if (this.$rootScope?.context?.profil?.admin) {
			this.menuOptions.push({
				text:  `<i class="fa fa-external-link"></i> ${this.$translate.instant('mf.goto-kid')}`,
				enabled: this.uniqueKid ? this.values : this.values && this.values.length > 0,
				click: () => {
					this.$location.path(`admin/oo/${this.uniqueKid ? this.values : this.values && this.values[0]?.kid}`);
				},
			});
		}

		if (!this.suggest && this.order) {
			this.suggest = this.order;
		}

		/**
		 * pop-up element used to display matches
		 *
		 * @type {$} jQuery Element
		 */
		// @ts-ignore
		this.$popUpElement = $('<mf-popup/>').attr({
			pw:                 'mf-popup',
			active:             '$ctrl.active',
			'assign-is-open':   '$ctrl.assignIsOpen(isOpen)',
			card:               '$ctrl.card',
			collapse:           '$ctrl.collapse(item)',
			create:             '$ctrl.create(event)',
			debounce:           '$ctrl.debounceUpdate',
			expand:             '$ctrl.expand(item)',
			id:                 popupId,
			matches:            '$ctrl.matches',
			majpressed:         '$ctrl.majpressed',
			'map-path':         '$ctrl.mapPath',
			'map-index':        '$ctrl.mapIndex',
			mf:                 '$ctrl',
			'move-in-progress': '$ctrl.moveInProgress',
			'multiple-selection': '$ctrl.multipleSelection',
			'open-form':        this.options?.openForm,
			options:            '$ctrl.options',
			order:              '$ctrl.order',
			position:           '$ctrl.position',
			query:              '$ctrl.query',
			select:             '$ctrl.select(item, event)',
			selectlist:         '$ctrl.selectlist',
			'show-suggestion':  '$ctrl.showSuggestion',
			values:             '$ctrl.values',
		});

		/** @type {any} */
		// @ts-ignore
		const valuesElement = $(this.$inputElement.parent().parent().find('.mf-values').get(0));
		valuesElement.attr({
			displayValues: 'displayValues',
			readOnly:      'readOnly',
			values:        'values',
		});
		this.$compile(valuesElement)(scope);

		/**
		 * Get the item from the specified path
		 *
		 * @param {any[]} path Path array
		 * @returns {any | null} Item
		 */
		const getItem = path => {
			let returnValue;
			if (path && path.length > 0) {
				// eslint-disable-next-line unicorn/no-reduce,unicorn/no-array-reduce
				path.reduce((result, key, index) => {
					if (index === path.length - 1) {
						returnValue = result[key];
					}
					// else {
					return result[key]?.matches;
					// }
				}, this.matches);
			}

			return returnValue;
		};

		/**
		 * Get the match item identifier
		 *
		 * @param {number} index Match index
		 * @returns {string} Item identifier
		 */
		const getMatchId = index => `${popupId}-option-${index}`;

		/**
		 * Indicate that the specified match is the active (pre-selected) item in the list owned by this MF.
		 * This attribute is added or removed automatically when the `active` changes.
		 *
		 * @param {any[]} active Active path
		 */
		const watchActive = active => {
			const item = getItem(active);
			if (item && item.path) {
				this.$inputElement.attr('aria-activedescendant', getMatchId(item.path.join('-')));
			}
			else {
				this.$inputElement.removeAttr('aria-activedescendant');
			}
		};
		scope.$watch('$ctrl.active', watchActive, true);

		this.resetMatches();

		this.#onKeyDown = event => {
			// mf is open and an "interesting" key was pressed
			if (
				(this.matches.length === 0 && !(this.hasFocus && event.code === 'ArrowDown' && !this.showSuggestion))
				|| !HOT_KEYS.has(event.code)
				|| this.loading
			) {
				return;
			}

			const shouldSelect = event.code === 'Enter' || event.code === 'Tab';

			const index = this.active.length > 0 ? this.active.at(-1) : -1;

			/**
			 * if there's nothing selected (i.e. focusFirst) and enter or tab is hit or shift + tab is
			 * pressed to bring focus to the previous element then clear the results
			 */
			if (((index < 0 && shouldSelect) || (event.code === 'Tab' && !!event.shiftKey)) && !!this.showSuggestion) {
				this.resetMatches();
				scope.$digest();
				return;
			}

			// event.preventDefault();
			let target;
			const item = getItem(this.active);

			switch (event.code) {
			case 'Escape': {
				// @ts-ignore
				if (this.$scope.mfPopup) {
					event.stopPropagation();
					if (this.useKeyboard) {
						this.useKeyboard = false;
						this.$inputElement.trigger('focus');
					}
					this.resetMatches();
					this.multipleSelection = this.options?.multipleSelection ?? false;
					this.$scope.$digest();
				}
				break;
			}

			case 'ArrowUp': {
				event.preventDefault();
				// @ts-ignore
				if (this.$scope.mfPopup) {
					if (this.mapIndex > 0) {
						this.active = [...this.mapPath[--this.mapIndex]] || [];

						// Automatic scroll popup on active element
						// @ts-ignore
						target = this.$popUpElement[0].querySelectorAll(`.mf-match-${this.active.join('-')}`);
						if (target.length > 0) {
							$(target).closest('.dropdown-menu')[0].scrollTop = target[0].offsetTop - 40;
						}
					}
					else if (!item || item.path[0] === 0) {
						// When the user reaches the top of matches list, go to the inside of input
						this.$inputElement.trigger('focus');
						this.active = [];
					}
					scope.$digest();
				}
				break;
			}

			case 'ArrowDown': {
				event.preventDefault();
				if (this.showSuggestion) {
					if (this.order || this.suggest) {
						(async () => this.showListSuggestions())();
						this.mapIndex = -1;
					}
				}
				else {
					if (this.mapIndex + 1 < this.mapPath.length) {
						if (!this.useKeyboard) {
							this.useKeyboard = true;
							this.$inputElement.trigger('blur');
						}
						this.active = [...this.mapPath[++this.mapIndex]] || [];
					}

					scope.$digest();

					// Automatic scroll popup on active element
					// @ts-ignore
					target = this.$popUpElement[0].querySelectorAll(`.mf-match-${this.active.join('-')}`);
					if (target.length > 0) {
						$(target).closest('.dropdown-menu')[0].scrollTop = target[0].offsetTop - 40;
					}
				}
				break;
			}

			case 'ArrowRight': {
				if (item && item.children === 1 && !item.expanded) {
					event.preventDefault();
					this.expand(item);
					scope.$digest();
				}
				break;
			}

			case 'ArrowLeft': {
				if (item && item.children === 1 && item.expanded) {
					event.preventDefault();
					this.collapse(item);
					scope.$digest();
				}
				break;
			}

			case 'AltLeft':
			case 'AltRight': {
				// ignore
				break;
			}

			case 'ControlLeft':
			case 'ControlRight': {
				// @ts-ignore
				if (this.$scope.mfPopup && this.card.to > 1) {
					this.multipleSelection = !this.multipleSelection;
					if (!this.multipleSelection && this.selectlist.length > 0) {
						for (const mItem of this.selectlist) {
							this.select(mItem.item, mItem.event);
						}
						this.selectlist.length = 0;
					}
					this.$scope.$apply();
				}
				break;
			}

			case 'ShiftLeft':
			case 'ShiftRight': {
				// @ts-ignore
				if (this.$scope.mfPopup && this.multipleSelection === true) {
					this.majpressed = true;
					this.$scope.$apply();
				}
				break;
			}

			default: {
				event.preventDefault();
				// @ts-ignore
				if (this.$scope.mfPopup && shouldSelect && item.selectable === 1) {
					this.hasFocus = false;
					scope.$apply(() => {
						if (typeof this.debounceUpdate === 'number' || typeof this.debounceUpdate === 'object') {
							this.$$debounce(() => {
								this.select(item, event);
							}, typeof this.debounceUpdate === 'number' ? this.debounceUpdate : this.debounceUpdate.default);
						}
						else {
							this.select(item, event);
						}
					});
				}
			}
			}
		};
		this.$window.addEventListener('keydown', this.#onKeyDown);

		this.#onKeyUp = event => {
			// mf is open and an "interesting" key was pressed
			if (this.matches.length === 0 || !HOT_KEYS.has(event.code)) {
				return;
			}

			switch (event.code) {
			case 'ShiftLeft': // release maj
			case 'ShiftRight': { // release maj
				this.majpressed = false;
				this.$scope.$apply();
				break;
			}
			default:
			}
		};
		this.$window.addEventListener('keyup', this.#onKeyUp);

		// @ts-ignore
		this.$popup = this.$compile(this.$popUpElement)(scope);

		if (this.options && this.kform && 'displayValues' in this.options && this.options.displayValues) {
			this.displayValues = this.card.to !== 1 || (this.formioComponent && this.formioComponent.readOnly);
		}

		if (this.kform) {
			const cForm = this.kform.forms[this.kform.currentClassForm];
			if (this.options && this.component.key in cForm.map && cForm.map[this.component.key].lastForm) {
				this.options.displayAddBtn = false;
			}
		}

		if (this.appendToBody) {
			this.$document.find('body').append(this.$popup);
		}
		else if (this.appendTo) {
			$(this.appendTo).eq(0).append(this.$popup);
		}
		else {
			this.$inputElement.closest('mf').append(this.$popup);
		}

		if (this.ngModelCtrl) {
			// @ts-ignore
			_.merge(this.ngModelOptions, this.ngModelCtrl.$options);
		}

		// @ts-ignore
		if (!this.$scope.builder && form && this.component) {
			// Recherche du link
			this.ktable.options.parent = {
				kid:        form.kid,
				isa:        form.isa,
				name:       form instanceof Form ? form.getName() : form.name,
				linker_kid: '0',
				create:     form.create,
			};
		}

		// @ts-ignore
		if (this.$scope.builder && 'query' in this.component) {
			delete this.ktable.query;
		}

		if (this.ngModelCtrl) {
			this.ngModelCtrl.$formatters.push(() => {
				if (this.deleting) {
					this.$timeout(() => {
						this.deleting = false;
					}, 1000);
				}
				else {
					// $timeout is needed here!
					this.$timeout(() => this.setValues());
				}
			});

			this.$scope.$watch('$ctrl.values', (newValues, oldValues) => {
				if (!_.isEqual(newValues, oldValues) && !this.deleting) {
					this.setValues();
				}
			});

			this.$scope.$watch('$ctrl.values', (newValues, oldValues) => {
				if (
					!_.isEqual(newValues, oldValues)
					&& ((Array.isArray(oldValues) && oldValues.length > 0) || oldValues !== undefined)
					&& ((Array.isArray(newValues) && newValues.length === 0) || newValues === undefined)
				) {
					this.mfInput = undefined;
				}
			}, true);

			if (this.ngModelOptions) {
				this.debounceUpdate = this.$parse(this.ngModelOptions.getOption('debounce'))(this.$scope);
			}

			if (this.mfValidate) {
				this.ngModelCtrl.$validators.required
					= (modelValue, viewValue) => this.validatorRequired(modelValue, viewValue);
				this.ngModelCtrl.$validators.card
					= (modelValue, viewValue) => this.validatorCard(modelValue, viewValue);
				this.ngModelCtrl.$validators.custom
					= (modelValue, viewValue) => this.validatorCustom(modelValue, viewValue);
			}

			this.ngModelCtrl.$validate();
		}
	}
}
